// Prompt 368 — FichaPersonalViewer: un solo componente A4 inline-editable
// para FP (Ficha de Puesto) y FR (Ficha de Rol Funcional).
// Reemplaza FichaPuestoViewer.tsx y FichaRolFuncionalViewer.tsx.

import { useState, useEffect, useRef, useCallback } from 'react';
import { useI18n } from '../../../../i18n';
import { useActiveDocument } from '../../../../context/ActiveDocumentContext';
import { C } from '@/lib/design';
import {
  fetchFichaContenido,
  fetchFichaRolFuncionalContenido,
  actualizarFichaContenido,
  actualizarFichaRolFuncionalContenido,
  transicionarEstadoVersion,
  solicitarAprobacionSign,
  fetchPuesto,
  type FichaContenido,
  type FichaSeccionesInput,
  type PuestoItem,
} from '../../../shared/services/api';
import {
  Campo,
  Seccion,
  estiloTitulo,
  estiloBtn,
  renderRelacionesJerarquicas,
  renderRolesGobernanza,
} from './FichaPersonalViewer.helpers';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';

// ── Tipos ───────────────────────────────────────────────────────────────────

export type FichaTipo = 'FP' | 'FR';

interface RolFuncionalBase {
  id: string;
  nombre: string;
  fichaDocumentoId: string | null;
}

export interface FichaPersonalViewerProps {
  tipo: FichaTipo;
  // FP
  puestoId?: string;
  puestoTitulo?: string;
  puestoNivel?: string | null;
  rolFuncional?: RolFuncionalBase | null;
  // FR
  rolId?: string;
  rolNombre?: string;
  rolNivel?: string | null;
  // Común
  editable?: boolean;
  onClose: () => void;
}

interface Secciones {
  mision: string;
  funciones: string;
  competenciasTecnicas: string;
  competenciasTransversales: string;
  formacion: string;
  experiencia: string;
  certificaciones: string;
  condiciones: string;
}

type SeccionKey = keyof Secciones;

const SECCIONES_VACIAS: Secciones = {
  mision: '',
  funciones: '',
  competenciasTecnicas: '',
  competenciasTransversales: '',
  formacion: '',
  experiencia: '',
  certificaciones: '',
  condiciones: '',
};

// ── Estilos (paleta V·IANet) ────────────────────────────────────────────────

const AZUL1 = C.azul1;
const AZUL2 = C.azul2;
const CELESTE = C.celeste;
const FONDO_SECCION = '#F8FAFC';
const BORDE = '#E5E7EB';
const PENDIENTE = '#9CA3AF';

const ESTADO_BADGE: Record<string, { bg: string; color: string; label: string }> = {
  BORRADOR: { bg: '#FFF7ED', color: '#C2410C', label: 'Borrador' },
  EN_REVISION: { bg: '#EFF6FF', color: '#1D4ED8', label: 'En revisión' },
  APROBADO: { bg: '#ECFDF5', color: '#059669', label: 'Aprobado' },
  PUBLICADO: { bg: '#F0FDF4', color: '#16A34A', label: 'Vigente' },
  OBSOLETO: { bg: '#FEF2F2', color: '#DC2626', label: 'Obsoleto' },
};

// ── Extracción de secciones desde ProseMirror ───────────────────────────────

const HEADING_TO_KEY: Record<string, SeccionKey> = {
  'misión del puesto': 'mision',
  'misión del rol': 'mision',
  'mision del puesto': 'mision',
  'mision del rol': 'mision',
  'funciones y responsabilidades': 'funciones',
  'competencias técnicas': 'competenciasTecnicas',
  'competencias tecnicas': 'competenciasTecnicas',
  'competencias transversales': 'competenciasTransversales',
  formación: 'formacion',
  formacion: 'formacion',
  experiencia: 'experiencia',
  certificaciones: 'certificaciones',
  'condiciones del puesto': 'condiciones',
  condiciones: 'condiciones',
};

interface PMNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: PMNode[];
  text?: string;
}

function textoPlanoDeNodo(node: PMNode): string {
  if (node.type === 'text') return node.text ?? '';
  if (!node.content) return '';
  return node.content.map(textoPlanoDeNodo).join('');
}

function extraerSecciones(contenidoJson: { content?: PMNode[] } | null | undefined): Secciones {
  const result: Secciones = { ...SECCIONES_VACIAS };
  if (!contenidoJson?.content) return result;

  let seccionActual: SeccionKey | null = null;
  const buffer: Record<SeccionKey, string[]> = {
    mision: [],
    funciones: [],
    competenciasTecnicas: [],
    competenciasTransversales: [],
    formacion: [],
    experiencia: [],
    certificaciones: [],
    condiciones: [],
  };

  for (const nodo of contenidoJson.content) {
    if (nodo.type === 'heading') {
      const texto = textoPlanoDeNodo(nodo).toLowerCase().trim();
      const key = HEADING_TO_KEY[texto] ?? null;
      seccionActual = key;
      continue;
    }
    if (nodo.type === 'paragraph' && seccionActual) {
      const texto = textoPlanoDeNodo(nodo).trim();
      // Ignorar placeholders del backend
      if (
        !texto ||
        texto.startsWith('(Pendiente') ||
        texto.startsWith('(Auto-generado') ||
        texto.startsWith('(Sin ') ||
        texto.startsWith('(sin ')
      ) {
        continue;
      }
      buffer[seccionActual].push(texto);
    }
  }

  for (const key of Object.keys(buffer) as SeccionKey[]) {
    result[key] = buffer[key].join('\n');
  }
  return result;
}

// ── Componente ──────────────────────────────────────────────────────────────

export function FichaPersonalViewer(props: FichaPersonalViewerProps) {
  const { t } = useI18n();
  const {
    tipo,
    puestoId,
    puestoTitulo,
    puestoNivel,
    rolFuncional,
    rolId,
    rolNombre,
    rolNivel,
    editable = false,
    onClose,
  } = props;

  const [ficha, setFicha] = useState<FichaContenido | null>(null);
  const [secciones, setSecciones] = useState<Secciones>(SECCIONES_VACIAS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [estadoActual, setEstadoActual] = useState<string>('');
  const [signState, setSignState] = useState<'idle' | 'sending' | 'error'>('idle');
  const [signError, setSignError] = useState<string>('');
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [puestoLive, setPuestoLive] = useState<PuestoItem | null>(null);

  const debounceRef = useRef<number | null>(null);
  const savedTimerRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  // ── Cargar ficha ────────────────────────────────────────────────────────

  const cargar = useCallback(() => {
    setLoading(true);
    setError('');
    const p =
      tipo === 'FP' && puestoId
        ? fetchFichaContenido(puestoId)
        : tipo === 'FR' && rolId
          ? fetchFichaRolFuncionalContenido(rolId)
          : Promise.reject(new Error('Identificador no proporcionado'));
    p.then((res) => {
      if (!mountedRef.current) return;
      setFicha(res.data);
      setSecciones(extraerSecciones(res.data.contenidoJson));
      setEstadoActual(res.data.estado);
    })
      .catch((err: unknown) => {
        if (!mountedRef.current) return;
        setError(err instanceof Error ? err.message : 'Error al cargar la ficha');
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });

    // Carga paralela de datos en vivo del puesto (FP) para relaciones y roles.
    if (tipo === 'FP' && puestoId) {
      fetchPuesto(puestoId)
        .then((res) => {
          if (mountedRef.current) setPuestoLive(res.data);
        })
        .catch(() => {
          if (mountedRef.current) setPuestoLive(null);
        });
    }
  }, [tipo, puestoId, rolId]);

  useEffect(() => {
    mountedRef.current = true;
    cargar();
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      if (savedTimerRef.current) window.clearTimeout(savedTimerRef.current);
    };
  }, [cargar]);

  // 389.M9 — Publicar documento activo (código FP-xxx o FR-xxx) cuando la ficha
  // se carga. Permite que VIAChatButton contextualice sobre la ficha en lugar
  // del proceso/puesto/rol padre. El cleanup usa el guard del 388c para no
  // borrar contexto que otro viewer ya publicó.
  const {
    setActiveDocument,
    clearActiveDocument,
    codigoDocumento: currentActiveCodigo,
  } = useActiveDocument();
  useEffect(() => {
    const tituloSegundario = tipo === 'FP' ? puestoTitulo : rolNombre;
    if (ficha?.codigo) {
      setActiveDocument(ficha.codigo, ficha.titulo ?? tituloSegundario ?? null);
    }
    return () => {
      if (ficha?.codigo && currentActiveCodigo === ficha.codigo) {
        clearActiveDocument();
      }
    };
  }, [
    ficha?.codigo,
    ficha?.titulo,
    tipo,
    puestoTitulo,
    rolNombre,
    currentActiveCodigo,
    setActiveDocument,
    clearActiveDocument,
  ]);

  // ── Guardado con debounce ───────────────────────────────────────────────

  const guardar = useCallback(
    async (next: Secciones) => {
      if (!editable) return;
      setSaveState('saving');
      try {
        const payload: FichaSeccionesInput = {
          mision: next.mision,
          funciones: next.funciones,
          competenciasTecnicas: next.competenciasTecnicas,
          competenciasTransversales: next.competenciasTransversales,
          formacion: next.formacion,
          experiencia: next.experiencia,
          certificaciones: next.certificaciones,
          ...(tipo === 'FP' ? { condiciones: next.condiciones } : {}),
        };
        if (tipo === 'FP' && puestoId) {
          await actualizarFichaContenido(puestoId, payload);
        } else if (tipo === 'FR' && rolId) {
          await actualizarFichaRolFuncionalContenido(rolId, payload);
        }
        if (!mountedRef.current) return;
        setSaveState('saved');
        if (savedTimerRef.current) window.clearTimeout(savedTimerRef.current);
        savedTimerRef.current = window.setTimeout(() => {
          if (mountedRef.current) setSaveState('idle');
        }, 2000);
      } catch {
        if (mountedRef.current) setSaveState('idle');
      }
    },
    [editable, tipo, puestoId, rolId],
  );

  const handleChange = useCallback(
    (key: SeccionKey, valor: string) => {
      setSecciones((prev) => {
        const next = { ...prev, [key]: valor };
        if (debounceRef.current) window.clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(() => guardar(next), 1500);
        return next;
      });
    },
    [guardar],
  );

  // ── Workflow ────────────────────────────────────────────────────────────

  const puedeEditar = editable && (estadoActual === 'BORRADOR' || estadoActual === 'EN_REVISION');

  const transicion = async (nuevoEstado: string) => {
    if (!ficha?.versionId) return;
    try {
      await transicionarEstadoVersion(ficha.versionId, nuevoEstado);
      setEstadoActual(nuevoEstado);
      cargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cambiar estado');
    }
  };

  const iniciarFirma = async () => {
    if (!ficha?.versionId) {
      setSignError('No hay versión asociada a esta ficha.');
      setSignState('error');
      return;
    }
    setSignError('');
    setSignState('sending');
    try {
      const resp = await solicitarAprobacionSign({
        entidadId: ficha.versionId,
        titulo:
          ficha.titulo ??
          (tipo === 'FP' ? (puestoTitulo ?? 'Ficha de Puesto') : (rolNombre ?? 'Ficha de Rol')),
      });
      if (!mountedRef.current) return;
      setSignState('idle');
      const count = resp.data?.notificadosCount ?? 0;
      const msg =
        count > 0
          ? t.structure.ficha.workflow.firmaSolicitadaN(count)
          : t.structure.ficha.workflow.firmaSolicitada;
      setToastMsg(msg);
      setTimeout(() => {
        if (mountedRef.current) setToastMsg(null);
      }, 4000);
      cargar();
    } catch (err) {
      if (!mountedRef.current) return;
      setSignError(err instanceof Error ? err.message : t.structure.ficha.workflow.errorFirma);
      setSignState('error');
    }
  };

  // ── Render helpers ──────────────────────────────────────────────────────

  const renderCampo = (key: SeccionKey, multiline = true, minHeight = 60) => {
    const valor = secciones[key];
    const placeholder = '(Pendiente de redacción)';
    if (!puedeEditar) {
      return valor ? (
        <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.7, color: '#1A1D23' }}>
          {valor}
        </div>
      ) : (
        <div style={{ fontSize: 14, color: PENDIENTE, fontStyle: 'italic' }}>{placeholder}</div>
      );
    }
    if (multiline) {
      return (
        <textarea
          value={valor}
          onChange={(e) => handleChange(key, e.target.value)}
          placeholder={placeholder}
          style={{
            width: '100%',
            minHeight,
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 14,
            lineHeight: 1.7,
            color: '#1A1D23',
            background: 'transparent',
            border: '1px solid transparent',
            borderRadius: 4,
            padding: 6,
            resize: 'vertical',
            outline: 'none',
          }}
          onFocus={(e) => (e.currentTarget.style.border = `1px solid ${CELESTE}`)}
          onBlur={(e) => (e.currentTarget.style.border = '1px solid transparent')}
        />
      );
    }
    return (
      <input
        type="text"
        value={valor}
        onChange={(e) => handleChange(key, e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%',
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 14,
          color: '#1A1D23',
          background: 'transparent',
          border: '1px solid transparent',
          borderRadius: 4,
          padding: 6,
          outline: 'none',
        }}
      />
    );
  };

  const badge = ESTADO_BADGE[estadoActual] ?? {
    bg: '#F3F4F6',
    color: '#6B7280',
    label: estadoActual,
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: 32,
        paddingBottom: 32,
        background: 'rgba(0,0,0,0.45)',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          width: 680,
          maxWidth: '95vw',
          boxShadow: '0 12px 48px rgba(0,0,0,0.2)',
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {/* Cabecera */}
        <div
          style={{
            padding: '20px 28px 14px',
            borderBottom: `1px solid ${BORDE}`,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span
                style={{
                  padding: '3px 10px',
                  borderRadius: 10,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  background: AZUL1,
                  color: '#fff',
                }}
              >
                {tipo === 'FP' ? t.structure.ficha.tipoBadgeFP : t.structure.ficha.tipoBadgeFR}
              </span>
              {ficha && (
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 12,
                    color: '#64748B',
                  }}
                >
                  {ficha.codigo}
                </span>
              )}
              <span
                style={{
                  padding: '3px 10px',
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 600,
                  background: badge.bg,
                  color: badge.color,
                }}
              >
                {badge.label}
              </span>
              {saveState === 'saving' && (
                <span style={{ fontSize: 11, color: '#64748B' }}>
                  {t.structure.ficha.guardando}
                </span>
              )}
              {saveState === 'saved' && (
                <span style={{ fontSize: 11, color: '#059669' }}>{t.structure.ficha.guardado}</span>
              )}
            </div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: AZUL1 }}>
              {tipo === 'FP' ? puestoTitulo : rolNombre}
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: '#64748B',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 0 }}>
          {loading && <LoadingSpinner size="md" muted />}
          {error && (
            <div style={{ textAlign: 'center', padding: 40, color: '#DC2626' }}>{error}</div>
          )}
          {!loading && !error && ficha && (
            <>
              {/* IDENTIFICACIÓN */}
              <section
                style={{
                  background: FONDO_SECCION,
                  padding: '16px 28px',
                  borderBottom: `1px solid ${BORDE}`,
                }}
              >
                <h3
                  style={{
                    margin: '0 0 10px',
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    color: AZUL2,
                    textTransform: 'uppercase',
                  }}
                >
                  Identificación
                </h3>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px 24px',
                    fontSize: 13,
                  }}
                >
                  {tipo === 'FP' && (
                    <>
                      <Campo label="Puesto" value={puestoTitulo ?? '—'} />
                      <Campo label="Nivel jerárquico" value={puestoNivel ?? '—'} />
                      {rolFuncional && <Campo label="Rol funcional" value={rolFuncional.nombre} />}
                    </>
                  )}
                  {tipo === 'FR' && (
                    <>
                      <Campo label="Rol funcional" value={rolNombre ?? '—'} />
                      <Campo label="Rango de nivel" value={rolNivel ?? '—'} />
                    </>
                  )}
                </div>
              </section>

              {/* SECCIONES DE CONTENIDO */}
              <Seccion titulo="Misión">{renderCampo('mision', true, 60)}</Seccion>

              <Seccion titulo="Funciones y responsabilidades">
                {renderCampo('funciones', true, 80)}
              </Seccion>

              {tipo === 'FP' && (
                <Seccion titulo="Relaciones jerárquicas">
                  {renderRelacionesJerarquicas(
                    puestoLive,
                    puestoTitulo,
                    t.structure.ficha.sinRelaciones,
                  )}
                </Seccion>
              )}

              {tipo === 'FP' && (
                <Seccion titulo="Roles de gobernanza">
                  {renderRolesGobernanza(puestoLive, t.structure.ficha.sinRoles)}
                </Seccion>
              )}

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 0,
                  borderBottom: `1px solid ${BORDE}`,
                }}
              >
                <div style={{ padding: '14px 28px', borderRight: `1px solid ${BORDE}` }}>
                  <h4 style={estiloTitulo}>Competencias técnicas</h4>
                  {renderCampo('competenciasTecnicas', true, 50)}
                </div>
                <div style={{ padding: '14px 28px' }}>
                  <h4 style={estiloTitulo}>Competencias transversales</h4>
                  {renderCampo('competenciasTransversales', true, 50)}
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: 0,
                  borderBottom: `1px solid ${BORDE}`,
                }}
              >
                <div style={{ padding: '14px 28px', borderRight: `1px solid ${BORDE}` }}>
                  <h4 style={estiloTitulo}>Formación</h4>
                  {renderCampo('formacion', true, 50)}
                </div>
                <div style={{ padding: '14px 28px', borderRight: `1px solid ${BORDE}` }}>
                  <h4 style={estiloTitulo}>Experiencia</h4>
                  {renderCampo('experiencia', true, 50)}
                </div>
                <div style={{ padding: '14px 28px' }}>
                  <h4 style={estiloTitulo}>Certificaciones</h4>
                  {renderCampo('certificaciones', true, 50)}
                </div>
              </div>

              {tipo === 'FP' && (
                <Seccion titulo="Condiciones del puesto">
                  {renderCampo('condiciones', true, 60)}
                </Seccion>
              )}
            </>
          )}
        </div>

        {/* Workflow */}
        {!loading && !error && ficha && (
          <div
            style={{
              borderTop: `1px solid ${BORDE}`,
              background: '#F9FAFB',
              borderBottomLeftRadius: 12,
              borderBottomRightRadius: 12,
            }}
          >
            {toastMsg && (
              <div
                style={{
                  padding: '10px 28px',
                  background: '#ECFDF5',
                  borderBottom: `1px solid ${BORDE}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  fontSize: 12,
                  color: '#059669',
                  fontWeight: 600,
                }}
              >
                <span>✓ {toastMsg}</span>
              </div>
            )}
            {signState === 'error' && signError && (
              <div
                style={{
                  padding: '10px 28px',
                  background: '#FEF2F2',
                  borderBottom: `1px solid ${BORDE}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  fontSize: 12,
                  color: '#DC2626',
                }}
              >
                <span>
                  {t.structure.ficha.workflow.errorFirma}: {signError}
                </span>
                <button
                  style={estiloBtn(false)}
                  onClick={iniciarFirma}
                  disabled={signState !== 'error'}
                >
                  {t.structure.ficha.workflow.reintentar}
                </button>
              </div>
            )}
            <div
              style={{
                padding: '14px 28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <span
                style={{
                  padding: '4px 12px',
                  borderRadius: 12,
                  fontSize: 11,
                  fontWeight: 600,
                  background: badge.bg,
                  color: badge.color,
                }}
              >
                {badge.label}
              </span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {editable && estadoActual === 'BORRADOR' && (
                  <button style={estiloBtn(true)} onClick={() => transicion('EN_REVISION')}>
                    {t.structure.ficha.workflow.enviarRevision}
                  </button>
                )}
                {editable && estadoActual === 'EN_REVISION' && (
                  <button style={estiloBtn(true)} onClick={() => transicion('APROBADO')}>
                    {t.structure.ficha.workflow.aprobar}
                  </button>
                )}
                {editable && estadoActual === 'APROBADO' && (
                  <button
                    style={estiloBtn(true)}
                    onClick={iniciarFirma}
                    disabled={signState === 'sending'}
                  >
                    {signState === 'sending'
                      ? t.structure.ficha.workflow.iniciandoFirma
                      : t.structure.ficha.workflow.iniciarFirma}
                  </button>
                )}
                <button style={estiloBtn(false)} onClick={onClose}>
                  {t.common.cerrar ?? 'Cerrar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Subcomponentes internos (Campo, Seccion), renderers en vivo y estilos se
// han extraído a `FichaPersonalViewer.helpers.tsx` (prompt 380 · O8).
